/**
 * SharePillContainer — binds the parent's `onRemove(userId)` to a zero-arg
 * handler so the view's Pressable gets a stable handler reference
 * (react-perf/jsx-no-new-function-as-prop). Shared by the desktop
 * ShareWatchlistModal and the MobileShareWatchlistSheet (SE-5527).
 * @module components/molecules/SharePill/SharePillContainer
 */
import React, { memo, useCallback } from "react";

import SharePillView from "./SharePillView";

/** Public props for the SharePill molecule. */
export interface SharePillProps {
  /** User id — passed back to `onRemove` when the pill is tapped. */
  readonly userId: string;
  /** Display name shown on the pill. */
  readonly name: string;
  /** Parent remove handler — receives the pill's userId. */
  readonly onRemove: (userId: string) => void;
  /** Visual variant — `solid` (desktop modal) or `tinted` (mobile sheet). */
  readonly variant: "solid" | "tinted";
  /** Test identity forwarded from the call site (e.g. `pillFor(userId)`). */
  readonly testID?: string;
}

/**
 * Binds the parent remove handler to this pill's userId and renders the view.
 * @param root0 - Component props
 * @param root0.userId - User id
 * @param root0.name - Display name
 * @param root0.onRemove - Parent remove handler keyed by userId
 * @param root0.variant - Visual variant
 * @param root0.testID - Test identity from the call site
 * @returns The rendered pill.
 */
const SharePillContainer = ({
  userId,
  name,
  onRemove,
  variant,
  testID,
}: SharePillProps) => {
  const handleRemove = useCallback(() => {
    onRemove(userId);
  }, [onRemove, userId]);

  return (
    <SharePillView
      name={name}
      onRemove={handleRemove}
      variant={variant}
      testID={testID}
    />
  );
};

SharePillContainer.displayName = "SharePillContainer";

export default memo(SharePillContainer);
